// openEHR BASE model — BMM package org.openehr.base.foundation_types.structure
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import type { Operation, T } from "../_shared.ts";
import { Any } from "./foundation_types.ts";
import { Boolean, Integer, String } from "./primitive_types.ts";
import type { Ordered } from "./primitive_types.ts";

/**
 * Abstract ancestor of container types whose items are addressable in some way.
 */
export abstract class Container<T extends Any> extends Any {
  /**
   * Test for membership of a value.
   * @param v - Parameter
   * @returns Result value
   */
  abstract has(v: T): Boolean;

  /**
   * Number of items in container.
   * @returns Result value
   */
  abstract count(): Integer;

  /**
   * True if container is empty.
   * @returns Result value
   */
  abstract is_empty(): Boolean;

  /**
   * Existential quantifier applied to container, taking one agent argument `_test_` whose signature is `(v:T): Boolean`.
   * @param test - Parameter
   * @returns Result value
   */
  abstract there_exists(test: Operation<T>): Boolean;

  /**
   * Universal quantifier applied to container, taking one agent argument `_test_` whose signature is `(v:T): Boolean`.
   * @param test - Parameter
   * @returns Result value
   */
  abstract for_all(test: Operation<T>): Boolean;

  /**
   * Return a List of all items matching the predicate function `_test_` which has signature `(v:T): Boolean`. 
   * If no matches, an empty List is returned.
   * Note: Return type corrected from BMM specification's `T` to `List<T>` to match specification intent.
   * @param test - Predicate function to test each item
   * @returns List of matching items
   */
  abstract matching(test: Operation<T>): List<T>;

  /**
   * Return first item matching the predicate function `_test_` which has signature `(v:T): Boolean`, or undefined if no match.
   * Note: Return type corrected to include `undefined` for TypeScript compatibility.
   * @param test - Predicate function to test each item
   * @returns First matching item or undefined
   */
  abstract select(test: Operation<T>): T | undefined;
}

/**
 * Type representing a keyed table of values. V is the value type, and K the type of the keys.
 */
export class Hash<K extends Ordered, V> extends Container<K> {
  private _map: Map<string, { key: K; value: V }> = new Map();

  /**
   * Constructor that optionally accepts initial entries.
   * @param entries - Optional array of [key, value] pairs
   */
  constructor(entries?: [K, V][]) {
    super();
    if (entries) {
      for (const [key, value] of entries) {
        this.put(key, value);
      }
    }
  }

  /**
   * Test for presence of \`_a_key_\`.
   * @param a_key - Parameter
   * @returns Result value
   */
  has_key(a_key: K): Boolean {
    const keyStr = this._keyToString(a_key);
    return new Boolean(this._map.has(keyStr));
  }

  /**
   * Return item for key \`_a_key_\`.
   * @param a_key - Parameter
   * @returns Result value
   */
  item(a_key: K): V {
    const keyStr = this._keyToString(a_key);
    const entry = this._map.get(keyStr);
    if (!entry) {
      throw new Error(`Key not found: ${keyStr}`);
    }
    return entry.value;
  }

  /**
   * Test for membership of a value (key in this case).
   */
  has(v: K): Boolean {
    return this.has_key(v);
  }

  /**
   * Return the number of items in the hash.
   */
  count(): Integer {
    const int = new Integer();
    int.value = this._map.size;
    return int;
  }

  /**
   * Check if the hash is empty.
   */
  is_empty(): Boolean {
    return new Boolean(this._map.size === 0);
  }

  /**
   * Test if all items satisfy a condition.
   */
  for_all(test: Operation<K>): Boolean {
    for (const entry of this._map.values()) {
      if (!test(entry.key).value) {
        return new Boolean(false);
      }
    }
    return new Boolean(true);
  }

  /**
   * Test if any item satisfies a condition.
   */
  there_exists(test: Operation<K>): Boolean {
    for (const entry of this._map.values()) {
      if (test(entry.key).value) {
        return new Boolean(true);
      }
    }
    return new Boolean(false);
  }

  /**
   * Check value equality with another object.
   */
  is_equal(other: Any): Boolean {
    if (!(other instanceof Hash)) {
      return new Boolean(false);
    }
    if (this._map.size !== other._map.size) {
      return new Boolean(false);
    }
    for (const [keyStr, entry] of this._map.entries()) {
      const otherEntry = other._map.get(keyStr);
      if (!otherEntry) {
        return new Boolean(false);
      }
      if (!entry.key.is_equal(otherEntry.key).value) {
        return new Boolean(false);
      }
      // Value comparison depends on type
      if (entry.value instanceof Any) {
        if (!entry.value.is_equal(otherEntry.value as Any).value) {
          return new Boolean(false);
        }
      } else if (entry.value !== otherEntry.value) {
        return new Boolean(false);
      }
    }
    return new Boolean(true);
  }

  /**
   * Put a key-value pair into the hash.
   */
  put(key: K, value: V): void {
    const keyStr = this._keyToString(key);
    this._map.set(keyStr, { key, value });
  }

  /**
   * Return a List of all keys matching the predicate function.
   * Note: Per openEHR specification, Hash<K,V> extends Container<K>, so matching operates on keys.
   * To search values, use item() to retrieve values for matched keys.
   * @param test - Predicate function with signature (v: K) => Boolean
   * @returns List of matching keys, empty list if no matches
   */
  override matching(test: Operation<K>): List<K> {
    const results = new List<K>();
    for (const entry of this._map.values()) {
      const testResult = test(entry.key);
      if (testResult?.value === true) {
        results.append(entry.key);
      }
    }
    return results;
  }

  /**
   * Return first key matching the predicate function, or undefined if no match.
   * Note: Per openEHR specification, Hash<K,V> extends Container<K>, so select operates on keys.
   * To retrieve the value, call item() with the returned key.
   * @param test - Predicate function with signature (v: K) => Boolean
   * @returns First matching key or undefined
   */
  override select(test: Operation<K>): K | undefined {
    for (const entry of this._map.values()) {
      const testResult = test(entry.key);
      if (testResult?.value === true) {
        return entry.key;
      }
    }
    return undefined;
  }

  /**
   * Helper to convert key to string for Map storage.
   */
  private _keyToString(key: K): string {
    if (key instanceof String) {
      return key.value || "";
    }
    // For other Ordered types, use a generic string representation
    return JSON.stringify(key);
  }
}

/**
 * Ordered container that may contain duplicates.
 */
export class List<T extends Any> extends Container<T> {
  private _items: T[] = [];

  /**
   * Test for membership of a value.
   */
  has(v: T): Boolean {
    for (const item of this._items) {
      if (item.is_equal(v).value === true) {
        return new Boolean(true);
      }
    }
    return new Boolean(false);
  }

  /**
   * Return the number of items in the list.
   */
  count(): Integer {
    const int = new Integer();
    int.value = this._items.length;
    return int;
  }

  /**
   * Check if the list is empty.
   */
  is_empty(): Boolean {
    return new Boolean(this._items.length === 0);
  }

  /**
   * Test if all items satisfy a condition.
   */
  for_all(test: Operation<T>): Boolean {
    for (const item of this._items) {
      if (!test(item).value) {
        return new Boolean(false);
      }
    }
    return new Boolean(true);
  }

  /**
   * Test if any item satisfies a condition.
   */
  there_exists(test: Operation<T>): Boolean {
    for (const item of this._items) {
      if (test(item).value) {
        return new Boolean(true);
      }
    }
    return new Boolean(false);
  }

  /**
   * Get the item at index i (0-based).
   */
  item(i: Integer): T {
    const idx = i.value;
    if (idx === undefined || idx < 0 || idx >= this._items.length) {
      throw new Error(`Index out of bounds: ${idx}`);
    }
    return this._items[idx];
  }

  /**
   * Return first element.
   * @returns Result value
   */
  first(): T {
    if (this._items.length === 0) {
      throw new Error("Cannot get first item of empty list");
    }
    return this._items[0];
  }

  /**
   * Return last element.
   * @returns Result value
   */
  last(): T {
    if (this._items.length === 0) {
      throw new Error("Cannot get last item of empty list");
    }
    return this._items[this._items.length - 1];
  }

  /**
   * Add an item to the end of the list.
   */
  append(v: T): void {
    this._items.push(v);
  }

  /**
   * Add an item to the beginning of the list.
   */
  prepend(v: T): void {
    this._items.unshift(v);
  }

  /**
   * Append all items from another list.
   */
  extend(other: List<T>): void {
    const otherCount = other.count().value;
    if (otherCount !== undefined) {
      for (let i = 0; i < otherCount; i++) {
        const idx = new Integer();
        idx.value = i;
        this._items.push(other.item(idx));
      }
    }
  }

  /**
   * Remove the item at index i.
   */
  remove(i: Integer): void {
    const idx = i.value;
    if (idx === undefined || idx < 0 || idx >= this._items.length) {
      throw new Error(`Index out of bounds: ${idx}`);
    }
    this._items.splice(idx, 1);
  }

  /**
   * Find the index of the first occurrence of a value.
   * Returns -1 if not found.
   */
  index_of(v: T): Integer {
    for (let i = 0; i < this._items.length; i++) {
      if (this._items[i].is_equal(v).value === true) {
        const idx = new Integer();
        idx.value = i;
        return idx;
      }
    }
    const idx = new Integer();
    idx.value = -1;
    return idx;
  }

  /**
   * Check value equality with another object.
   */
  is_equal(other: Any): Boolean {
    if (!(other instanceof List)) {
      return new Boolean(false);
    }
    if (this._items.length !== other._items.length) {
      return new Boolean(false);
    }
    for (let i = 0; i < this._items.length; i++) {
      if (!this._items[i].is_equal(other._items[i]).value) {
        return new Boolean(false);
      }
    }
    return new Boolean(true);
  }

  /**
   * Return a List of all items matching the predicate function.
   * @param test - Predicate function with signature (v: T) => Boolean
   * @returns List of matching items, empty list if no matches
   */
  override matching(test: Operation<T>): List<T> {
    const results = new List<T>();
    for (const item of this._items) {
      const testResult = test(item);
      if (testResult?.value === true) {
        results.append(item);
      }
    }
    return results;
  }

  /**
   * Return first item matching the predicate function, or undefined if no match.
   * @param test - Predicate function with signature (v: T) => Boolean
   * @returns First matching item or undefined
   */
  override select(test: Operation<T>): T | undefined {
    for (const item of this._items) {
      const testResult = test(item);
      if (testResult?.value === true) {
        return item;
      }
    }
    return undefined;
  }
}

/**
 * Unordered container that may not contain duplicates.
 */
export class Set<T extends Any> extends Container<T> {
  private _items: T[] = [];

  /**
   * Test for membership of a value.
   */
  has(v: T): Boolean {
    for (const item of this._items) {
      if (item.is_equal(v).value === true) {
        return new Boolean(true);
      }
    }
    return new Boolean(false);
  }

  /**
   * Return the number of items in the set.
   */
  count(): Integer {
    const int = new Integer();
    int.value = this._items.length;
    return int;
  }

  /**
   * Check if the set is empty.
   */
  is_empty(): Boolean {
    return new Boolean(this._items.length === 0);
  }

  /**
   * Test if all items satisfy a condition.
   */
  for_all(test: Operation<T>): Boolean {
    for (const item of this._items) {
      if (!test(item).value) {
        return new Boolean(false);
      }
    }
    return new Boolean(true);
  }

  /**
   * Test if any item satisfies a condition.
   */
  there_exists(test: Operation<T>): Boolean {
    for (const item of this._items) {
      if (test(item).value) {
        return new Boolean(true);
      }
    }
    return new Boolean(false);
  }

  /**
   * Add an item to the set if it doesn't already exist.
   */
  add(v: T): void {
    if (!this.has(v).value) {
      this._items.push(v);
    }
  }

  /**
   * Remove an item from the set.
   */
  remove(v: T): void {
    for (let i = 0; i < this._items.length; i++) {
      if (this._items[i].is_equal(v).value === true) {
        this._items.splice(i, 1);
        return;
      }
    }
  }

  /**
   * Check value equality with another object.
   */
  is_equal(other: Any): Boolean {
    if (!(other instanceof Set)) {
      return new Boolean(false);
    }
    if (this._items.length !== other._items.length) {
      return new Boolean(false);
    }
    // Check if all items in this set are in the other set
    for (const item of this._items) {
      if (!other.has(item).value) {
        return new Boolean(false);
      }
    }
    return new Boolean(true);
  }

  /**
   * Return a List of all items matching the predicate function.
   * @param test - Predicate function with signature (v: T) => Boolean
   * @returns List of matching items, empty list if no matches
   */
  override matching(test: Operation<T>): List<T> {
    const results = new List<T>();
    for (const item of this._items) {
      const testResult = test(item);
      if (testResult?.value === true) {
        results.append(item);
      }
    }
    return results;
  }

  /**
   * Return first item matching the predicate function, or undefined if no match.
   * @param test - Predicate function with signature (v: T) => Boolean
   * @returns First matching item or undefined
   */
  override select(test: Operation<T>): T | undefined {
    for (const item of this._items) {
      const testResult = test(item);
      if (testResult?.value === true) {
        return item;
      }
    }
    return undefined;
  }
}

/**
 * Container whose storage is assumed to be contiguous.
 */
export class Array<T extends Any> extends Container<T> {
  private _items: T[] = [];

  /**
   * Test for membership of a value.
   */
  has(v: T): Boolean {
    for (const item of this._items) {
      if (item.is_equal(v).value === true) {
        return new Boolean(true);
      }
    }
    return new Boolean(false);
  }

  /**
   * Return the number of items in the array.
   */
  count(): Integer {
    const int = new Integer();
    int.value = this._items.length;
    return int;
  }

  /**
   * Check if the array is empty.
   */
  is_empty(): Boolean {
    return new Boolean(this._items.length === 0);
  }

  /**
   * Test if all items satisfy a condition.
   */
  for_all(test: Operation<T>): Boolean {
    for (const item of this._items) {
      if (!test(item).value) {
        return new Boolean(false);
      }
    }
    return new Boolean(true);
  }

  /**
   * Test if any item satisfies a condition.
   */
  there_exists(test: Operation<T>): Boolean {
    for (const item of this._items) {
      if (test(item).value) {
        return new Boolean(true);
      }
    }
    return new Boolean(false);
  }

  /**
   * Return item for key  \`_a_key_\`.
   * @param a_key - Parameter
   * @returns Result value
   */
  item(a_key: Integer): T {
    const idx = a_key.value;
    if (idx === undefined || idx < 0 || idx >= this._items.length) {
      throw new Error(`Index out of bounds: ${idx}`);
    }
    return this._items[idx];
  }

  /**
   * Check value equality with another object.
   */
  is_equal(other: Any): Boolean {
    if (!(other instanceof Array)) {
      return new Boolean(false);
    }
    if (this._items.length !== other._items.length) {
      return new Boolean(false);
    }
    for (let i = 0; i < this._items.length; i++) {
      if (!this._items[i].is_equal(other._items[i]).value) {
        return new Boolean(false);
      }
    }
    return new Boolean(true);
  }

  /**
   * Set the item at index i.
   */
  put(i: Integer, v: T): void {
    const idx = i.value;
    if (idx === undefined || idx < 0 || idx >= this._items.length) {
      throw new Error(`Index out of bounds: ${idx}`);
    }
    this._items[idx] = v;
  }

  /**
   * Append an item to the array.
   */
  append(v: T): void {
    this._items.push(v);
  }

  /**
   * Return a List of all items matching the predicate function.
   * @param test - Predicate function with signature (v: T) => Boolean
   * @returns List of matching items, empty list if no matches
   */
  override matching(test: Operation<T>): List<T> {
    const results = new List<T>();
    for (const item of this._items) {
      const testResult = test(item);
      if (testResult?.value === true) {
        results.append(item);
      }
    }
    return results;
  }

  /**
   * Return first item matching the predicate function, or undefined if no match.
   * @param test - Predicate function with signature (v: T) => Boolean
   * @returns First matching item or undefined
   */
  override select(test: Operation<T>): T | undefined {
    for (const item of this._items) {
      const testResult = test(item);
      if (testResult?.value === true) {
        return item;
      }
    }
    return undefined;
  }
}
